import { createLogger, Logger } from '../utils/Logger';

export interface ProjectileStats {
  readonly speed: number;
  readonly damage: number;
  readonly life: number;
  readonly pierce: number;
  readonly spread: number;
  readonly area: number;
  readonly count: number;
}

export type WeaponBehaviorType = 'projectile' | 'orbit' | 'area' | 'beam' | 'boomerang' | 'chain';

export interface BurnModifierContent {
  readonly damagePerSecond: number;
  readonly duration: number;
}

export interface SlowModifierContent {
  readonly multiplier: number;
  readonly duration: number;
}

export interface ChainLightningModifierContent {
  readonly maxTargets: number;
  readonly radius: number;
  readonly falloff?: number;
}

export interface WeaponModifierContent {
  readonly bonusDamage?: number;
  readonly areaMultiplier?: number;
  readonly critChance?: number;
  readonly burn?: BurnModifierContent;
  readonly slow?: SlowModifierContent;
  readonly chainLightning?: ChainLightningModifierContent;
  readonly chainHit?: number;
}

export interface WeaponStats extends ProjectileStats {
  readonly range?: number;
  readonly duration?: number;
  readonly tickRate?: number;
  readonly returnSpeed?: number;
  readonly chainHit?: number;
  readonly modifiers?: WeaponModifierContent;
}

export interface WeaponLevel {
  readonly level: number;
  readonly projectile: Partial<WeaponStats>;
  readonly fireRate: number;
  readonly description?: string;
  readonly descriptionKey?: string;
}

export interface WeaponContent {
  readonly id: string;
  readonly name: string;
  readonly nameKey?: string;
  readonly descriptionKey?: string;
  readonly type: WeaponBehaviorType;
  readonly sprite: string;
  readonly baseStats: WeaponStats;
  readonly baseFireRate: number;
  readonly levels: readonly WeaponLevel[];
  readonly tags: readonly string[];
}

export interface PassiveModifier {
  readonly damageMultiplier?: number;
  readonly cooldownMultiplier?: number;
  readonly maxHealth?: number;
  readonly moveSpeed?: number;
  readonly pickupRange?: number;
}

export interface PassiveLevel {
  readonly level: number;
  readonly modifiers: PassiveModifier;
  readonly descriptionKey: string;
}

export interface PassiveContent {
  readonly id: string;
  readonly nameKey: string;
  readonly icon: string;
  readonly color: number;
  readonly levels: readonly PassiveLevel[];
}

export interface EvolutionRecipeContent {
  readonly id: string;
  readonly baseWeaponId: string;
  readonly requiredPassiveId: string;
  readonly requiredPassiveLevel: number;
  readonly evolvedWeaponId: string;
}

export interface ConsumableContent {
  readonly id: string;
  readonly nameKey: string;
  readonly descriptionKey: string;
  readonly type: 'heal' | 'gold';
  readonly value: number;
  readonly color: number;
  readonly icon: string;
}

export interface EnemyDropContent {
  readonly type: 'gem' | 'chest';
  readonly chance: number;
  readonly value?: number;
}

export interface EnemyBehaviorParams {
  readonly dashCooldown?: number;
  readonly dashDuration?: number;
  readonly dashSpeedMultiplier?: number;
  readonly dashMinDistance?: number;
  readonly dashMaxDistance?: number;
  readonly rangedPreferredDistance?: number;
  readonly rangedRetreatDistance?: number;
}

export interface EnemyStatScalingModifier {
  readonly multiplier?: number;
  readonly additive?: number;
}

export interface WaveScalingModifiersContent {
  readonly hp?: EnemyStatScalingModifier;
  readonly speed?: EnemyStatScalingModifier;
  readonly damage?: EnemyStatScalingModifier;
  readonly xpReward?: EnemyStatScalingModifier;
}

export interface EnemyContent {
  readonly id: string;
  readonly name: string;
  readonly stats: {
    readonly hp: number;
    readonly speed: number;
    readonly damage: number;
    readonly xpReward: number;
  };
  readonly sprite: string;
  readonly aiPattern: 'chase' | 'ranged' | 'dash' | 'boss';
  readonly behaviorParams?: EnemyBehaviorParams;
  readonly drops?: readonly EnemyDropContent[];
  readonly scale?: number;
  readonly color?: number;
}

export interface WaveSpawnContent {
  readonly enemyId: string;
  readonly count: number;
  readonly pattern: 'random' | 'circle' | 'cluster';
  readonly modifiers: readonly string[];
  readonly scaling?: WaveScalingModifiersContent;
  readonly params?: Readonly<Record<string, unknown>>;
}

export interface WaveContent {
  readonly time: number;
  readonly spawns: readonly WaveSpawnContent[];
}

export interface BossEncounterStatModifiersContent {
  readonly damageMultiplier?: number;
  readonly speedMultiplier?: number;
  readonly xpRewardMultiplier?: number;
}

export type BossEncounterRewardMode = 'enemyDrops' | 'guaranteedChest' | 'guaranteedGems';

export interface BossEncounterRewardPolicyContent {
  readonly mode: BossEncounterRewardMode;
  readonly chestCount?: number;
  readonly gemMultiplier?: number;
}

export interface BossEncounterContent {
  readonly id: string;
  readonly spawnTime: number;
  readonly enemyId: string;
  readonly hpMultiplier?: number;
  readonly statModifiers?: BossEncounterStatModifiersContent;
  readonly rewardPolicy?: BossEncounterRewardPolicyContent;
  readonly displayName?: string;
  readonly displayNameKey?: string;
  readonly warningTextKey?: string;
  readonly requiredForVictory?: boolean;
}

export interface BossVictoryRequirementsContent {
  readonly requiredEncounterIds: readonly string[];
  readonly requiredBossCount: number;
}

export interface CombatFeedbackAudioCueContent {
  readonly key: string;
  readonly volume?: number;
}

export interface CombatFeedbackEffectContent {
  readonly particleBurstCount?: number;
  readonly particleIntensity?: number;
  readonly shakeDurationMs?: number;
  readonly shakeIntensity?: number;
  readonly flashDurationMs?: number;
  readonly flashAlpha?: number;
  readonly tintDurationMs?: number;
  readonly sound?: CombatFeedbackAudioCueContent;
}

export interface CombatFeedbackContent {
  readonly hit?: CombatFeedbackEffectContent;
  readonly crit?: CombatFeedbackEffectContent;
  readonly burn?: CombatFeedbackEffectContent;
  readonly slow?: CombatFeedbackEffectContent;
  readonly chain?: CombatFeedbackEffectContent;
  readonly area?: CombatFeedbackEffectContent;
  readonly enemyDeath?: CombatFeedbackEffectContent;
  readonly bossDeath?: CombatFeedbackEffectContent;
  readonly playerHit?: CombatFeedbackEffectContent;
  readonly bossWarning?: CombatFeedbackEffectContent;
  readonly levelUp?: CombatFeedbackEffectContent;
  readonly chestReward?: CombatFeedbackEffectContent;
  readonly evolutionReveal?: CombatFeedbackEffectContent;
}

export interface GameSettingsContent {
  readonly experience?: { readonly baseXp: number; readonly growthFactor: number };
  readonly bossSpawnTime: number;
  readonly killBossToWin: boolean;
  readonly bossesToWin: number;
  readonly bossEnemyId: string;
  readonly bossEncounters?: readonly BossEncounterContent[];
  readonly bossVictoryRequirements?: BossVictoryRequirementsContent;
  readonly starterWeaponIds: readonly string[];
  readonly availableWeaponIds: readonly string[];
  readonly maxWeapons: number;
  readonly maxPassives: number;
  readonly consumableFallbackIds: readonly string[];
  readonly combatFeedback: CombatFeedbackContent;
}

export interface BiomeContent {
  readonly id: string;
  readonly name: string;
  readonly backgroundColor: string;
  readonly gridColor: string;
  readonly distanceThreshold?: number;
}

export interface ContentRegistryInput {
  readonly weapons: readonly WeaponContent[];
  readonly passives: readonly PassiveContent[];
  readonly evolutionRecipes: readonly EvolutionRecipeContent[];
  readonly consumables: readonly ConsumableContent[];
  readonly enemies: readonly EnemyContent[];
  readonly biomes: readonly BiomeContent[];
  readonly waveTable: readonly WaveContent[];
  readonly gameSettings: GameSettingsContent;
}

export class ContentRegistry {
  private readonly weapons: ReadonlyMap<string, WeaponContent>;
  private readonly passives: ReadonlyMap<string, PassiveContent>;
  private readonly evolutionRecipesByBaseWeapon: ReadonlyMap<string, EvolutionRecipeContent>;
  private readonly consumables: ReadonlyMap<string, ConsumableContent>;
  private readonly enemies: ReadonlyMap<string, EnemyContent>;
  private readonly biomes: ReadonlyMap<string, BiomeContent>;
  private readonly waveTable: readonly WaveContent[];
  private readonly gameSettings: GameSettingsContent;
  private readonly logger: Logger;

  constructor(input: ContentRegistryInput, logger: Logger = createLogger('ContentRegistry')) {
    this.logger = logger;
    this.logger.debug('constructor:start', {
      weapons: input.weapons.length,
      passives: input.passives.length,
      evolutionRecipes: input.evolutionRecipes.length,
      consumables: input.consumables.length,
      enemies: input.enemies.length,
      biomes: input.biomes.length,
      waves: input.waveTable.length
    });

    this.weapons = this.indexById(input.weapons, 'weapon');
    this.passives = this.indexById(input.passives, 'passive');
    this.evolutionRecipesByBaseWeapon = new Map(
      input.evolutionRecipes.map(recipe => [recipe.baseWeaponId, deepFreeze(cloneContent(recipe))])
    );
    this.consumables = this.indexById(input.consumables, 'consumable');
    this.enemies = this.indexById(input.enemies, 'enemy');
    this.biomes = this.indexById(input.biomes, 'biome');
    this.logger.info('constructor:biomes-loaded', {
      count: this.biomes.size,
      biomeIds: Array.from(this.biomes.keys())
    });
    this.waveTable = deepFreeze(cloneContent(input.waveTable));
    this.gameSettings = deepFreeze(cloneContent(input.gameSettings));

    this.logger.info('constructor:complete', this.getSummary());
  }

  public getWeapon(id: string): WeaponContent | undefined {
    return this.lookup(this.weapons, 'weapon', id);
  }

  public hasWeapon(id: string): boolean {
    const result = this.weapons.has(id);
    this.logger.debug('hasWeapon', { id, result });
    return result;
  }

  public getWeapons(): readonly WeaponContent[] {
    const weapons = Array.from(this.weapons.values());
    this.logger.debug('getWeapons', { count: weapons.length });
    return weapons;
  }

  public getPassive(id: string): PassiveContent | undefined {
    return this.lookup(this.passives, 'passive', id);
  }

  public getPassives(): readonly PassiveContent[] {
    const passives = Array.from(this.passives.values());
    this.logger.debug('getPassives', { count: passives.length });
    return passives;
  }

  public getEnemy(id: string): EnemyContent | undefined {
    return this.lookup(this.enemies, 'enemy', id);
  }

  public findEnemy(id: string): EnemyContent | undefined {
    return this.optionalLookup(this.enemies, 'enemy', id);
  }

  public hasEnemy(id: string): boolean {
    this.logger.debug('hasEnemy:start', { id });
    const result = this.enemies.has(id);
    this.logger.debug('hasEnemy:complete', { id, result });
    return result;
  }

  public getEnemies(): readonly EnemyContent[] {
    const enemies = Array.from(this.enemies.values());
    this.logger.debug('getEnemies', { count: enemies.length });
    return enemies;
  }

  public getBiome(id: string): BiomeContent | undefined {
    return this.lookup(this.biomes, 'biome', id);
  }

  public findBiome(id: string): BiomeContent | undefined {
    return this.optionalLookup(this.biomes, 'biome', id);
  }

  public getBiomes(): readonly BiomeContent[] {
    const biomes = Array.from(this.biomes.values());
    this.logger.debug('getBiomes', { count: biomes.length });
    return biomes;
  }

  public getWaveTable(): readonly WaveContent[] {
    this.logger.debug('getWaveTable', { waves: this.waveTable.length });
    return this.waveTable;
  }

  public getGameSettings(): GameSettingsContent {
    this.logger.debug('getGameSettings', {
      bossSpawnTime: this.gameSettings.bossSpawnTime,
      killBossToWin: this.gameSettings.killBossToWin,
      bossesToWin: this.gameSettings.bossesToWin,
      bossEnemyId: this.gameSettings.bossEnemyId,
      bossEncounters: this.gameSettings.bossEncounters?.map(encounter => ({
        id: encounter.id,
        enemyId: encounter.enemyId,
        spawnTime: encounter.spawnTime,
        requiredForVictory: encounter.requiredForVictory !== false
      })),
      bossVictoryRequirements: this.gameSettings.bossVictoryRequirements,
      maxWeapons: this.gameSettings.maxWeapons,
      maxPassives: this.gameSettings.maxPassives,
      starterWeaponIds: this.gameSettings.starterWeaponIds,
      availableWeaponIds: this.gameSettings.availableWeaponIds,
      consumableFallbackIds: this.gameSettings.consumableFallbackIds
    });
    return this.gameSettings;
  }

  public getStarterWeaponIds(): readonly string[] {
    this.logger.debug('getStarterWeaponIds', { ids: this.gameSettings.starterWeaponIds });
    return this.gameSettings.starterWeaponIds;
  }

  public getAvailableWeaponIds(): readonly string[] {
    this.logger.debug('getAvailableWeaponIds', { ids: this.gameSettings.availableWeaponIds });
    return this.gameSettings.availableWeaponIds;
  }

  public getEvolutionRecipe(baseWeaponId: string): EvolutionRecipeContent | undefined {
    return this.lookup(this.evolutionRecipesByBaseWeapon, 'evolution', baseWeaponId);
  }

  public findEvolutionRecipe(baseWeaponId: string): EvolutionRecipeContent | undefined {
    return this.optionalLookup(this.evolutionRecipesByBaseWeapon, 'evolution', baseWeaponId);
  }

  public getConsumable(id: string): ConsumableContent | undefined {
    return this.lookup(this.consumables, 'consumable', id);
  }

  public getConsumables(): readonly ConsumableContent[] {
    const consumables = Array.from(this.consumables.values());
    this.logger.debug('getConsumables', { count: consumables.length });
    return consumables;
  }

  public getSummary(): Record<string, number> {
    return {
      weapons: this.weapons.size,
      passives: this.passives.size,
      evolutionRecipes: this.evolutionRecipesByBaseWeapon.size,
      consumables: this.consumables.size,
      enemies: this.enemies.size,
      biomes: this.biomes.size,
      waves: this.waveTable.length
    };
  }

  private indexById<T extends { readonly id: string }>(items: readonly T[], type: string): ReadonlyMap<string, T> {
    this.logger.debug('indexById:start', { type, count: items.length });
    const index = new Map<string, T>();

    for (const item of items) {
      if (index.has(item.id)) {
        this.logger.warn('indexById:duplicate-id-overwrite', { type, id: item.id });
      }
      index.set(item.id, deepFreeze(cloneContent(item)));
    }

    this.logger.debug('indexById:complete', { type, count: index.size });
    return index;
  }

  private lookup<T>(index: ReadonlyMap<string, T>, type: string, id: string): T | undefined {
    this.logger.debug('lookup:start', { type, id });
    const result = index.get(id);

    if (!result) {
      this.logger.error('lookup:missing-required-content', { type, id });
      return undefined;
    }

    this.logger.debug('lookup:complete', { type, id });
    return result;
  }

  private optionalLookup<T>(index: ReadonlyMap<string, T>, type: string, id: string): T | undefined {
    this.logger.debug('optionalLookup:start', { type, id });
    const result = index.get(id);
    this.logger.debug('optionalLookup:complete', { type, id, found: !!result });
    return result;
  }
}

function cloneContent<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function deepFreeze<T>(value: T): T {
  if (!isFreezable(value) || Object.isFrozen(value)) {
    return value;
  }

  for (const child of Object.values(value as Record<string, unknown>)) {
    deepFreeze(child);
  }

  return Object.freeze(value);
}

function isFreezable(value: unknown): value is object {
  return (typeof value === 'object' || typeof value === 'function') && value !== null;
}
